import { NormalButton, CustomButton } from '@/shared/ui/components';
import { DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { dateToString } from '@/shared/lib';
import { Timeline } from 'antd';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { useLabelInformation } from '../../../model/labelInformation';
import DownloadLabelIcon from './assets/download-label-icon.svg';
import formatShippingLabel from './handler';
import type { LabelInformationProps } from './labelInformation.types';
import './styles.scss';

const EMPTY_ARRAY: any[] = [];

const LabelInformation = ({
  rate,
  shipmentDate,
  trackingHistory,
  transaction,
  index,
  stateCA = EMPTY_ARRAY,
  stateUS = EMPTY_ARRAY,
  country: countryProp = EMPTY_ARRAY,
  id,
  timezone,
  studyFid,
  handleRefreshTracking,
}: LabelInformationProps) => {
  const { state, setState, onDownloadLink } = useLabelInformation({ transaction });

  const shippingLabel = formatShippingLabel(index, studyFid);

  /**
   * Builds the human-readable address shown for a shipment tracking event on the return status tab. A null location yields the double-hyphen bullet, otherwise the country and the US or Canadian state codes are resolved to their labels and street, city, state and country are joined with commas, skipping every empty part.
   *
   * @param location - Tracking event location carrying street1, city, state and country.
   * @returns Comma-joined address string, or the placeholder bullet when no location exists.
   */
  const formatLocation = (location: any) => {
    if (location === null) {
      return DOUBLE_HYPHEN_BULLET;
    }
    const { city, country, state: stateLocation, street1 } = location;
    const newCountry = _.find(countryProp, ['value', country]);
    const newState = _.find(country === 'US' ? stateUS : stateCA, ['value', stateLocation]);
    const newLocation = [];
    if (!_.isEmpty(street1)) {
      newLocation.push(street1);
    }
    if (!_.isEmpty(city)) {
      newLocation.push(city);
    }
    if (!_.isEmpty(newState)) {
      newLocation.push(newState.label);
    }
    if (!_.isEmpty(newCountry)) {
      newLocation.push(newCountry.label);
    }
    return _.join(newLocation, ', ');
  };

  const orderedTrackingHistory = _.orderBy(trackingHistory, ['statusDate'], ['desc']);

  return (
    <div className="label-information-wrapper-v2">
      <div className="label-information-table">
        <div className="label-information">
          <div className="title-row">
            <span>{`${t`Label`} #${index + 1}`}</span>
          </div>
          <div className="label-information-content">
            <div className="left-column">
              <div className="item-row">
                <div className="title">{`${t`Tracking number`}:`}</div>
                <div className="content">
                  <a
                    className="number"
                    href={transaction?.trackingUrlProvider || '#'}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {transaction?.trackingNumber || DOUBLE_HYPHEN_BULLET}
                  </a>
                </div>
              </div>
              <div className="item-row">
                <div className="title">{`${t`Shipped date`}:`}</div>
                <div className="content">
                  <div className="date">
                    {shipmentDate
                      ? dateToString({
                          isoDate: shipmentDate,
                          timezone,
                          format: 'MM/DD/YYYY',
                        })
                      : DOUBLE_HYPHEN_BULLET}
                  </div>
                </div>
              </div>
              <div className="item-row">
                <div className="title">{`${t`Estimated arrival`}:`}</div>
                <div className="content">
                  <div className="date">
                    {transaction?.eta
                      ? dateToString({
                          isoDate: transaction?.eta,
                          timezone,
                          format: 'MM/DD/YYYY',
                        })
                      : DOUBLE_HYPHEN_BULLET}
                  </div>
                </div>
              </div>
            </div>
            <div className="right-column">
              <div className="item-row">
                <div className="title">{`${t`Label`}:`}</div>
                <div className="content">
                  <div className="shipping-label">
                    <div
                      onClick={() => {
                        onDownloadLink(shippingLabel, index);
                      }}
                      id={`${shippingLabel}-${index}`}
                      className="btn-download-label"
                      aria-hidden
                    >
                      {shippingLabel}
                    </div>
                    <CustomButton
                      id="download-link-btn"
                      icon={<ReactSVG src={DownloadLabelIcon} />}
                      onClick={() => {
                        onDownloadLink(shippingLabel, index);
                      }}
                      loading={state.isLoadingPDF}
                      style="neutral"
                      type="tertiary"
                      size="small"
                      ariaLabel={t`Download label`}
                    />
                  </div>
                </div>
              </div>
              <div className="item-row">
                <div className="title">{`${t`Label details`}:`}</div>
                <div className="content">
                  <div className="label-details">
                    <div className="delivery-unit">
                      <img src={rate?.carrierLogo200} alt="Carrier logo" />
                      <span>{`${rate?.carrier} ${rate?.serviceLevelName}`}</span>
                    </div>
                    <div className="cost">{`$${(rate?.amount || 0).toFixed(2)}`}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="device-timeline">
          <div className="timeline">
            <div className="title-row">
              <span>{t`Timeline`}</span>
              <NormalButton
                id="refresh-btn"
                isText
                isBold
                buttonName={t`Refresh`}
                onClick={() => {
                  setState({ isRefreshingTracking: true });
                  handleRefreshTracking({
                    transactionId: transaction?.id,
                    ticketId: id,
                    index,
                    callback: () => {
                      setState({ isRefreshingTracking: false });
                    },
                  });
                }}
              />
            </div>
            <div className="timeline-box">
              <div className="date-container">
                {state.isRefreshingTracking ? (
                  <Skeleton
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: 100,
                    }}
                  />
                ) : (
                  <Timeline mode="left" aria-label={t`Tracking history`}>
                    {!_.isEmpty(orderedTrackingHistory) &&
                      orderedTrackingHistory?.map((item) => (
                        <Timeline.Item color="#EAEDF1">
                          <div className="status-bar">
                            <div className="status">
                              {item.status
                                ? item.status === 'UNKNOWN'
                                  ? 'Pre-transit'
                                  : _.lowerCase(item.status)
                                : DOUBLE_HYPHEN_BULLET}
                            </div>
                            <div className="time">
                              {item.statusDate
                                ? dateToString({
                                    isoDate: item.statusDate,
                                    timezone,
                                    format: 'MM/DD/YYYY',
                                  })
                                : DOUBLE_HYPHEN_BULLET}
                            </div>
                            <div className="address">{formatLocation(item.location)}</div>
                            <div className="status-detail">{item.statusDetails}</div>
                          </div>
                        </Timeline.Item>
                      ))}
                  </Timeline>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LabelInformation;
